<template>
  <div class="pdf-workbench-container">
    <!-- Header - Simulation Mode -->
    <v-app-bar v-if="isSimulationMode" color="white" elevation="1" app>
      <v-btn icon @click="exitSimulation" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title>
        <div class="d-flex flex-column">
          <span class="font-weight-bold">PDF Simulation Mode</span>
          <span v-if="vault && file" class="text-caption text--secondary">
            {{ vault.name }} / {{ file.name }}
          </span>
        </div>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" outlined @click="exitSimulation">
        <v-icon left>mdi-exit-to-app</v-icon>
        Return to Portal
      </v-btn>
    </v-app-bar>
    
    <!-- Header - Sign Mode -->
    <v-app-bar v-else-if="isSignMode" color="white" elevation="1" app>
      <v-toolbar-title>
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Sign Document</span>
          <span v-if="signatureRequest" class="text-caption text--secondary">
            {{ signatureRequest.file_name }}
          </span>
        </div>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip v-if="recipientEmail" class="mr-2" small color="primary">
        <v-icon small left>mdi-account</v-icon>
        {{ recipientEmail }}
      </v-chip>
      <v-btn color="primary" outlined @click="exitSimulation">
        <v-icon left>mdi-exit-to-app</v-icon>
        Return to Portal
      </v-btn>
    </v-app-bar>
    
    <!-- Main Content Area -->
    <v-main>
    <!-- Simulation Mode Banner -->
    <v-alert
      v-if="isSimulationMode"
      prominent
      type="warning"
      color="orange darken-2"
      dark
      class="simulation-banner mb-0"
      style="border-radius: 0;"
    >
      <v-row align="center">
        <v-col class="grow">
          <div class="text-h6 font-weight-bold">
            <v-icon large left>mdi-test-tube</v-icon>
            SIMULATION MODE - NO CHANGES WILL BE SAVED
          </div>
          <div class="text-body-2 mt-1">
            This is a test mode for vault administrators. You can interact with the PDF fields, but nothing will be saved to the actual document.
          </div>
        </v-col>
      </v-row>
    </v-alert>
    
    <!-- Loading State -->
    <div v-if="loading" class="workbench-content d-flex justify-center align-center">
      <v-card max-width="400" class="pa-6 text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <h3 class="mt-4">{{ isSignMode ? 'Loading signature request...' : 'Loading PDF simulation...' }}</h3>
        <p class="text--secondary mt-2">Please wait while we prepare {{ isSignMode ? 'your document' : 'the document' }}.</p>
      </v-card>
    </div>
    
    <!-- Error State -->
    <div v-else-if="errorMessage" class="workbench-content d-flex justify-center align-center">
      <v-card max-width="500" class="pa-6">
        <v-icon large color="warning" class="mb-4">mdi-information-outline</v-icon>
        <h2 class="mb-2">{{ isSignMode ? 'Unable to Load Document' : 'Unable to Load Simulation' }}</h2>
        <p class="text--secondary">{{ errorMessage }}</p>
        <v-btn color="primary" class="mt-4" @click="returnToPortalFromError">
          Return to Portal
        </v-btn>
      </v-card>
    </div>
    
    <!-- Completion State (Sign Mode Only) -->
    <div v-else-if="isSignMode && completed" class="workbench-content d-flex justify-center align-center">
      <v-card max-width="600" class="pa-8 text-center">
        <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
        <h2 class="success--text mb-2">Signature Complete!</h2>
        <p class="text-h6 mb-4">Thank you for signing the document.</p>
        <p class="text-body-1">
          A confirmation email has been sent to {{ recipientEmail }}.
        </p>
        <v-btn color="primary" large class="mt-6" @click="$router.push('/')">
          Done
        </v-btn>
      </v-card>
    </div>
    
    <!-- Main Content -->
    <div v-else-if="vault && file && pdfUrl" class="workbench-content">
      <v-row no-gutters class="fill-height workbench-row">
        <!-- Left Panel - Instructions and Progress -->
        <v-col cols="12" md="3" class="workbench-sidebar pa-4 grey lighten-4">
          <div class="mb-4">
            <h3 class="mb-2">{{ isSignMode ? 'Instructions' : 'Simulation Instructions' }}</h3>
            <p class="text-body-2">
              {{ isSignMode 
                ? 'Click Start to begin the guided signing process. The yellow arrow will guide you through each required field.' 
                : 'Test the "Sign & Return" experience by selecting different participants and filling out their assigned fields.' 
              }}
            </p>
          </div>
          
          <!-- Participant Selection (Simulation Only) -->
          <v-card v-if="isSimulationMode" outlined class="mb-4">
            <v-card-text v-if="participants.length > 0 || portalMembers.length > 0">
              <h4 class="mb-3">Active Participant</h4>
              <v-select
                v-model="selectedParticipant"
                :items="participantOptions"
                item-text="label"
                item-value="value"
                label="Simulate as..."
                outlined
                dense
                @change="changeParticipant"
              >
                <template v-slot:selection="{ item }">
                  <v-icon v-if="item.isOrganization" small color="white" class="mr-2">mdi-domain</v-icon>
                  <v-icon v-else-if="item.isAnyGuest" small color="white" class="mr-2">mdi-account-group</v-icon>
                  <v-chip
                    v-else-if="item.value !== 'all' && item.value !== 'any_guest'"
                    small
                    :color="item.color"
                    dark
                    class="mr-2"
                  >
                    {{ item.order }}
                  </v-chip>
                  <span>{{ item.label }}</span>
                  <v-chip
                    v-if="item.isSelf"
                    x-small
                    color="green"
                    dark
                    class="ml-2"
                  >
                    You
                  </v-chip>
                  <v-chip
                    v-if="item.isOrganization"
                    x-small
                    color="primary"
                    dark
                    class="ml-2"
                  >
                    Organization
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-icon v-if="item.isOrganization" small class="mr-2" color="primary">mdi-domain</v-icon>
                  <v-icon v-else-if="item.isAnyGuest" small class="mr-2" color="cyan">mdi-account-group</v-icon>
                  <v-chip
                    v-else-if="item.value !== 'all' && item.value !== 'any_guest'"
                    small
                    :color="item.color"
                    dark
                    class="mr-2"
                  >
                    {{ item.order }}
                  </v-chip>
                  <span>{{ item.label }}</span>
                  <v-chip
                    v-if="item.isSelf"
                    x-small
                    color="green"
                    dark
                    class="ml-2"
                  >
                    You
                  </v-chip>
                  <v-chip
                    v-if="item.isOrganization"
                    x-small
                    color="primary"
                    outlined
                    class="ml-2"
                  >
                    Organization
                  </v-chip>
                  <v-chip
                    v-if="item.isAnyGuest"
                    x-small
                    color="cyan"
                    outlined
                    class="ml-2"
                  >
                    Any Guest
                  </v-chip>
                </template>
              </v-select>
              <p class="text-caption text--secondary mt-2">
                {{ selectedParticipant === 'all' ? 'Viewing all signature fields for all participants. All form fields are editable.' : 'Only signature fields assigned to this participant will be visible. All form fields remain editable.' }}
              </p>
              <div v-if="selectedParticipant !== 'all' && getSelectedParticipantInfo()" class="mt-2">
                <v-chip
                  x-small
                  :color="getSelectedParticipantInfo().color"
                  dark
                  class="mr-1"
                >
                  {{ getSelectedParticipantInfo().userType || 'guest' }}
                </v-chip>
                <v-chip
                  v-if="getSelectedParticipantInfo().isSelf"
                  x-small
                  color="green"
                  dark
                >
                  You
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
          
          <!-- Progress Indicator -->
          <v-card outlined class="mb-4" v-if="guidedSigningReady && activeParticipantProgress.total > 0">
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="font-weight-bold">
                  {{ isSignMode ? 'Progress' : (selectedParticipant === 'all' ? 'Total Progress' : 'Participant Progress') }}
                </span>
                <span class="text-body-2 font-weight-bold" :class="{'success--text': activeParticipantProgress.completed === activeParticipantProgress.total}">
                  {{ activeParticipantProgress.completed }} / {{ activeParticipantProgress.total }} Completed
                </span>
              </div>
              <v-progress-linear
                :value="activeParticipantProgress.percentage"
                :color="activeParticipantProgress.completed === activeParticipantProgress.total ? 'success' : 'primary'"
                height="10"
                rounded
                striped
              ></v-progress-linear>
              <div class="text-caption text--secondary mt-1 text-right">
                {{ activeParticipantProgress.percentage }}% Done
              </div>
            </v-card-text>
          </v-card>
          
          <!-- File Info (Simulation Only) -->
          <v-card v-if="isSimulationMode" outlined class="mb-4">
            <v-card-text>
              <h4 class="mb-2">Document</h4>
              <div class="text-body-2">
                <strong>File:</strong> {{ file.name }}<br>
                <strong>Portal:</strong> {{ vault.name }}<br>
                <strong>Portal Members:</strong> {{ portalMembers.length }}
              </div>
            </v-card-text>
          </v-card>
          
          <!-- Required Fields List -->
          <v-card outlined class="mb-4" v-if="requiredFieldsList.length > 0">
            <v-card-text>
              <h4 class="mb-2">Required Fields ({{ requiredFieldsList.length }})</h4>
              <v-list dense class="pa-0">
                <v-list-item
                  v-for="(field, index) in requiredFieldsList"
                  :key="field.id"
                  class="px-0"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small :color="field.filled ? 'success' : field.participantColor">
                      {{ field.filled ? 'mdi-check-circle' : getFieldIcon(field.fieldType) }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-caption">
                      <span :class="{'text-decoration-line-through grey--text': field.filled}">
                        {{ field.displayName || field.fieldName || field.subject }}
                      </span>
                      <span v-if="!field.filled"> - Page {{ field.pageNumber }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="isSimulationMode" class="text-caption">
                      <span v-if="field.participantEmail === 'any_guest' || field.isAnyGuest">
                        <v-icon x-small class="mr-1" color="cyan">mdi-account-group</v-icon>
                        Any Guest
                      </span>
                      <span v-else-if="field.participantEmail && field.participantEmail.startsWith('org:')">
                        <v-icon x-small class="mr-1">mdi-domain</v-icon>
                        {{ organization ? organization.companyName : 'Organization' }}
                      </span>
                      <span v-else>
                        {{ field.participantEmail && field.participantEmail !== 'signer@simulation.local' ? field.participantEmail : 'Unassigned' }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
          
          <!-- Custom Message (Sign Mode Only) -->
          <v-card v-if="isSignMode && signatureRequest && signatureRequest.custom_message" outlined class="mb-4">
            <v-card-text>
              <h4 class="mb-2">Message from Sender</h4>
              <p class="text-body-2">{{ signatureRequest.custom_message }}</p>
            </v-card-text>
          </v-card>
          
          <!-- Signature Fields by Participant (Simulation Only) -->
          <v-card v-if="isSimulationMode" outlined class="mb-4">
            <v-card-text v-if="signatureFieldsList.length > 0">
              <h4 class="mb-2">Signature Field Assignments ({{ signatureFieldsList.length }})</h4>
              <p class="text-caption text--secondary mb-3">
                Assign signature fields to participants. These assignments will be saved with the file.
              </p>
              <div
                v-for="field in signatureFieldsList"
                :key="field.id"
                class="mb-3 pa-2"
                style="border: 1px solid #e0e0e0; border-radius: 4px;"
              >
                <div class="d-flex align-center mb-1">
                  <v-icon small :color="field.participantColor" class="mr-2">
                    mdi-draw
                  </v-icon>
                  <span class="text-caption font-weight-bold">
                    {{ field.displayName || field.fieldName || 'Signature' }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-chip x-small color="grey lighten-2">
                    Page {{ field.pageNumber }}
                  </v-chip>
                </div>
                <v-select
                  :value="field.participantEmail || field.participantId"
                  :items="portalMembersForAssignment"
                  item-text="label"
                  item-value="email"
                  label="Assigned to"
                  dense
                  outlined
                  hide-details
                  @change="(value) => updateFieldAssignment(field, value)"
                  class="mt-2"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip
                      x-small
                      :color="item.color"
                      dark
                      class="mr-1"
                    >
                      {{ item.order }}
                    </v-chip>
                    <span class="text-caption">{{ item.name }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-chip
                      x-small
                      :color="item.color"
                      dark
                      class="mr-2"
                    >
                      {{ item.order }}
                    </v-chip>
                    <span>{{ item.label }}</span>
                    <v-chip
                      v-if="item.isSelf"
                      x-small
                      color="green"
                      dark
                      class="ml-2"
                    >
                      You
                    </v-chip>
                  </template>
                </v-select>
              </div>
            </v-card-text>
          </v-card>
          
          <!-- Navigation Controls -->
          <v-card outlined v-if="guidedSigningReady && totalFields > 0">
            <v-card-text>
              <h4 class="mb-3">Field Navigation</h4>
              <v-btn
                block
                x-large
                :color="nextButtonText === 'Finish' ? 'success' : 'primary'"
                @click="handleNextButton"
                :disabled="isSignMode && submitting"
                class="mb-2"
              >
                <v-icon left>
                  {{ nextButtonText === 'Start' ? 'mdi-play' : nextButtonText === 'Finish' ? 'mdi-check-circle' : 'mdi-arrow-down' }}
                </v-icon>
                {{ nextButtonText }}
              </v-btn>
              <v-btn
                block
                outlined
                @click="navigateToPreviousField"
                :disabled="currentFieldIndex === 0"
              >
                <v-icon left>mdi-arrow-up</v-icon>
                Previous Field
              </v-btn>
            </v-card-text>
          </v-card>
          
          <!-- Actions (Simulation Only) -->
          <v-card v-if="isSimulationMode" outlined class="mt-4">
            <v-card-text>
              <h4 class="mb-3">Simulation Actions</h4>
              <v-btn
                block
                color="success"
                @click="showSimulationResults"
                class="mb-2"
              >
                <v-icon left>mdi-eye</v-icon>
                Show What Would Be Saved
              </v-btn>
              <v-btn
                block
                color="error"
                outlined
                @click="exitSimulation"
              >
                <v-icon left>mdi-close</v-icon>
                Exit Without Saving
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Right Panel - PDF Viewer -->
        <v-col cols="12" md="9" class="pa-0 pdf-viewer-col">
          <div class="pdf-viewer-wrapper">
            <web-viewer
              v-if="pdfUrl"
              ref="pdfViewer"
              :doc-url="pdfUrl"
              :file="file"
              :vault="vault"
              :simulation-mode="isSimulationMode"
              :signature-mode="true"
              :signature-request-id="signatureRequestId"
              :required-fields-xfdf="signatureRequest ? signatureRequest.required_fields_xfdf : undefined"
              :field-assignments="computedFieldAssignments"
              :recipient-email="recipientEmail"
              @guidedSigningReady="onGuidedSigningReady"
              @fieldNavigated="onFieldNavigated"
              @allFieldsReviewed="onAllFieldsReviewed"
              @fieldStatusChanged="onFieldStatusChanged"
              @updateNextButtonText="updateNextButtonText"
              @signatureCompleted="onSignatureCompleted"
              @finishButtonClicked="handleNextButton"
            ></web-viewer>
          </div>
        </v-col>
      </v-row>
    </div>
    
    <!-- Results Dialog (Simulation Only) -->
    <v-dialog v-model="showResults" max-width="800" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="info">mdi-information</v-icon>
          Simulation Results
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-alert type="info" text class="mb-4">
            <strong>Important:</strong> In a real signature workflow, this data would be saved to the PDF. 
            Since this is simulation mode, nothing has been saved.
          </v-alert>
          
          <div class="mb-4">
            <h3 class="mb-2">What Would Be Saved:</h3>
            <p class="text-body-2">
              The following XFDF data contains all the form field values and annotations you entered:
            </p>
          </div>
          
          <v-card outlined>
            <v-card-text>
              <pre class="simulation-results-xfdf">{{ simulationResults && simulationResults.xfdfString ? simulationResults.xfdfString : 'No data available' }}</pre>
            </v-card-text>
          </v-card>
          
          <div class="mt-4">
            <h3 class="mb-2">Summary:</h3>
            <ul>
              <li>Document: <strong>{{ file ? file.name : '' }}</strong></li>
              <li>Portal: <strong>{{ vault ? vault.name : '' }}</strong></li>
              <li>Fields Modified: <strong>{{ totalFields }}</strong></li>
              <li>Status: <strong class="orange--text">NOT SAVED (Simulation Only)</strong></li>
            </ul>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeResults">
            Continue Simulation
          </v-btn>
          <v-btn color="primary" @click="exitSimulation">
            Exit Simulation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-main>
  </div>
</template>

<script>
import WebViewer from '@/components/WebViewer.vue';
import pdfSignWorkbenchController from '@/controllers/pdfSignWorkbenchController';

export default {
  ...pdfSignWorkbenchController,
  components: {
    WebViewer
  }
};
</script>

<style scoped>
.pdf-workbench-container {
  background-color: #f5f5f5;
  height: 100vh;
  width: 100%; /* Changed from 100vw to 100% to avoid scrollbar clipping */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Ensure full screen takeover */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
}

/* Ensure all intermediate containers pass through height */
.pdf-workbench-container >>> .v-application--wrap {
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-workbench-container >>> .v-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  max-width: none !important; /* Override Vuetify's default max-width */
}

.pdf-workbench-container >>> .v-main__wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
}

.simulation-banner {
  flex-shrink: 0;
}

.workbench-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.workbench-content >>> .workbench-row {
  flex: 1;
  margin: 0;
  /* overflow: hidden; - Removed to prevent clipping scrollbars */
  min-height: 0;
  height: 100%;
  width: 100%;
  max-width: none;
  display: flex !important;
}

.workbench-sidebar {
  border-right: 1px solid #ddd;
  overflow-y: auto;
  max-height: 100%;
}

.pdf-viewer-col {
  display: flex !important;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  flex: 1;
}

.pdf-viewer-wrapper {
  position: relative;
  background-color: white;
  flex: 1;
  /* overflow: hidden; - Removed to allow scrolling if needed, though PDFTron handles it */
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pdf-viewer-wrapper >>> .viewer-container {
  height: 100%;
  min-height: 0;
  flex: 1;
}

.pdf-viewer-wrapper >>> div[ref='viewer'] {
  height: 100%;
  min-height: 0;
  flex: 1;
}

.simulation-results-xfdf {
  max-height: 300px;
  overflow-y: auto;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Mobile Responsive Adjustments */
@media (max-width: 959px) {
  .pdf-workbench-container {
    height: auto !important;
    min-height: 100vh;
    overflow-y: auto; /* Allow window scrolling on mobile */
    position: absolute; /* Unfix it so it can scroll */
  }
  
  .workbench-content {
    overflow: visible;
    flex: none;
    height: auto;
  }
  
  .workbench-content >>> .workbench-row {
    flex-direction: column;
    flex: none;
    height: auto;
  }

  .workbench-sidebar {
    max-height: 350px;
    border-right: none;
    border-bottom: 1px solid #ddd;
    flex: none;
  }
  
  .pdf-viewer-col {
    height: 80vh; /* Give substantial fixed height on mobile */
    flex: none;
  }
  
  .pdf-workbench-container >>> .v-main {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }
  
  .pdf-workbench-container >>> .v-main__wrap {
    height: auto;
    overflow: visible;
  }
}
</style>
